import { Box, Stack, Typography } from "@mui/material";
// config
import colors from "../../../config/colors";

const CategoryDistribution = ({ distribution = [] }) => {
  const sortedDistribution = [...distribution]?.sort((a, b) => {
    const percentageA = a.percentage || 0;
    const percentageB = b.percentage || 0;
    return percentageB - percentageA;
  });

  const totalPercentage = sortedDistribution?.reduce(
    (sum, item) => sum + (item.percentage || 0),
    0,
  );

  const segments = sortedDistribution?.map((item, index) => {
    const percentage = item.percentage || 0;
    const angle = (percentage / totalPercentage) * 360;
    return { ...item, angle, index };
  });

  // Chart dimensions
  const size = 100;
  const radius = 40;
  const strokeWidth = 11;
  const centerX = size / 2;
  const centerY = size / 2;

  const categoryColors = ["#FFAB7C", "#9772FF", "#FF7B9A", "#FFDB7D"];

  const generateColor = (index) => {
    return categoryColors[index % categoryColors?.length];
  };

  const createArcPath = (startAngle, endAngle, radius) => {
    // Handle full circle case (360 degrees)
    if (endAngle - startAngle >= 360) {
      return `M ${centerX} ${centerY - radius} A ${radius} ${radius} 0 1 1 ${centerX} ${centerY + radius} A ${radius} ${radius} 0 1 1 ${centerX} ${centerY - radius}`;
    }

    const startRad = (startAngle - 90) * (Math.PI / 180);
    const endRad = (endAngle - 90) * (Math.PI / 180);

    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);

    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
  };

  let currentAngle = 0;
  const isSingleCategory = segments.length === 1;

  return (
    <Box
      sx={{
        padding: "8px 12px",
        border: `1px solid ${colors.gray100}`,
        borderRadius: "4px",
        backgroundColor: colors.white,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Typography
        fontSize={12}
        fontWeight={400}
        lineHeight="20px"
        color={colors.gray600}
      >
        Category Distribution:
      </Typography>
      <Stack direction={"row"} gap={2} alignItems="center">
        <Box sx={{ position: "relative" }}>
          <svg width={size} height={size}>
            {!isSingleCategory && (
              <circle
                cx={centerX}
                cy={centerY}
                r={radius}
                fill="none"
                stroke={colors.gray100}
                strokeWidth={strokeWidth}
              />
            )}

            {segments.map((segment, index) => {
              const startAngle = currentAngle;
              const endAngle = currentAngle + segment.angle;
              currentAngle = endAngle;

              // For single category, draw a full circle
              if (isSingleCategory) {
                return (
                  <circle
                    key={index}
                    cx={centerX}
                    cy={centerY}
                    r={radius}
                    fill="none"
                    stroke={generateColor(index)}
                    strokeWidth={strokeWidth}
                  />
                );
              }

              return (
                <path
                  key={index}
                  d={createArcPath(startAngle, endAngle, radius)}
                  fill="none"
                  stroke={generateColor(index)}
                  strokeWidth={strokeWidth}
                />
              );
            })}
          </svg>
        </Box>

        <Stack
          gap={1}
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "16px",
            rowGap: "8px",
            // flex: 1,
          }}
        >
          {sortedDistribution?.map((item, index) => (
            <Stack key={index} direction={"row"} gap={1} alignItems="center">
              <Box
                sx={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: generateColor(index),
                  flexShrink: 0,
                }}
              />
              <Typography
                fontSize={12}
                fontWeight={400}
                color={colors.gray600}
                lineHeight="20px"
                flexShrink={0}
              >
                {item?.category || "Others"}
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={500}
                lineHeight="20px"
                sx={{ flexShrink: 0 }}
              >
                {item?.percentage?.toFixed(1).replace(/\.0$/, "")}%
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};

export default CategoryDistribution;
